import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import Field from 'ui/Field';
import Icon from 'ui/Icon';
import Input from 'ui/Input';
import Tooltip from 'ui/Tooltip';
import { MAX_TEXT_VALUE_LENGTH } from 'utils/validation';

import { DELAYED_INPUT_DEBOUNCE_MS } from '../../../../constants';
import { useSankeyExcelBindingContext } from '../../../../contexts/sankeyExcelBindingContext';
import { useSankeyFormContext } from '../../../../contexts/sankeyFormContext';
import { SankeyNode } from '../../../../diagram/types';
import { useIsDataFromExcel } from '../../../DataEditor/utils';
import { renameNode, useClashingNamesForNode } from './utils';

interface Props {
  node: SankeyNode;
}

const NodeRenameControl: React.FC<Props> = ({ node }) => {
  const { form } = useSankeyFormContext();
  const isDataFromExcel = useIsDataFromExcel();
  const clashingNodeNames = useClashingNamesForNode(form, node);
  const { hasExcelBindingInThisSheet } = useSankeyExcelBindingContext();

  const [nodeName, setNodeName] = useState(node.name);
  const [nodeNameError, setNodeNameError] = useState('');

  const infoText = hasExcelBindingInThisSheet
    ? 'To edit the label change the value in the Excel sheet'
    : undefined;

  // Update the error message with the debounce and propagate the new name to the form only if it's valid.
  const debouncedPropagateName = useMemo(
    () =>
      debounce((newName: string) => {
        if (newName !== newName.trim()) {
          setNodeNameError('Cannot start or end with space');
        } else if (!newName.trim()) {
          setNodeNameError('Cannot be empty');
        } else if (clashingNodeNames.includes(newName)) {
          setNodeNameError('This label clashes with another node');
        } else {
          renameNode(form, node.id, newName);
          setNodeNameError('');
        }
      }, DELAYED_INPUT_DEBOUNCE_MS),
    [form, node.id, clashingNodeNames]
  );

  // Update the node name in the temporary variable before propagating it to the form.
  // This lets us validate it before updating the form and it also speeds up editing a lot.
  const handleRename = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setNodeName(ev.target.value);
      debouncedPropagateName(ev.target.value);
    },
    [debouncedPropagateName]
  );

  return (
    <Field
      label={
        <>
          Label{' '}
          {infoText && (
            <Tooltip content={infoText}>
              <Icon color="blue.600" cursor="help" icon="question-circle" />
            </Tooltip>
          )}
        </>
      }
      disabled={isDataFromExcel}
      invalid={!!nodeNameError}
      errorText={nodeNameError}
      fontSize="xs"
    >
      <Tooltip content={infoText}>
        <Input
          value={nodeName}
          onChange={handleRename}
          maxLength={MAX_TEXT_VALUE_LENGTH}
          size="sm"
        />
      </Tooltip>
    </Field>
  );
};

export default NodeRenameControl;
